import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface PipelineProgressionItem {
  state: string;
  label: string;
  color: string;
  count: number;
}

interface PipelineProgressionChartProps {
  data: PipelineProgressionItem[];
  loading?: boolean;
  title?: string;
}

// Custom tooltip component
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
        <p className="font-medium text-gray-900">{data.label}</p>
        <p className="text-sm text-gray-600">
          {data.count.toLocaleString()} events
        </p>
      </div>
    );
  }
  return null;
};

export function PipelineProgressionChart({
  data,
  loading,
  title = 'Pipeline Stage Progression',
}: PipelineProgressionChartProps) {
  // Calculate total for display
  const total = data.reduce((sum, item) => sum + item.count, 0);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="h-48 flex items-center justify-center">
          <div className="animate-pulse flex gap-2 items-end">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-gray-200 rounded"
                style={{
                  width: '40px',
                  height: `${30 + Math.random() * 60}px`,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (data.length === 0 || total === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="h-48 flex items-center justify-center text-gray-500">
          <p>No pipeline stage data available for this period</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <span className="text-sm text-gray-500">
          {total.toLocaleString()} total events
        </span>
      </div>

      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="label" hide />
            <YAxis hide />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend with counts */}
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        {data.map((item, index) => (
          <div
            key={index}
            className="flex items-center gap-2 text-sm bg-gray-50 rounded-lg px-2.5 py-1.5"
          >
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-gray-600 truncate flex-1">{item.label}</span>
            <span className="text-gray-900 font-semibold">
              {item.count.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
